/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import { StateObservable, ofType } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { concatMap, map, mergeMap, switchMap } from 'rxjs/operators';
import type { Action } from '@reduxjs/toolkit';
import type { AxiosResponse } from 'axios';

import { getResolvedTargetName } from '../../router/routing-manager';
import * as actions from '../../store/state/actions/action-creators';
import * as StateManager from '../../store/state/state-manager';
import { processDataToDispatchErrorAction } from '../../store/state/epics/util';
import { isErrorActionType, promiseHandler, deferCaseSummaryViewUpdate } from '../common-helper';
import { executeReleaseLockRequest } from '../release-lock/async-request';
import {
  closeContainerItem,
  getActiveContainerItemName,
  getChildContainers,
  getContainerData,
  getTargetFromContainerItemID,
  isContainerSingleMode
} from '../../container/container-utils';
import { getParentContextName } from '../../utils/context-utils';
import { updateAvailableActionsInResponse } from '../../utils/dxapi-response-translator';
import RemoteCase from '../remote-case';
import type { ErrorAction, State } from '../../types';
import CaseUtils from '../utils';
import ActionsSequencer from '../../actions/sequencer';

import { SAVE_AND_CLOSE } from './action-creator';
import { executeSaveAssignmentRequest } from './async-request';
import type { ActionWithPayload, PerformAndSaveActionObject, PerformAndSaveActionPayload } from './types';
import { publishSaveAssignmentCompleteEvent } from './events';

export const performSave = (saveActionObject: PerformAndSaveActionObject, saveOnly = false) => {
  const { action, state$, actionMgrID } = saveActionObject;
  let actionsOut: ActionWithPayload<PerformAndSaveActionPayload>[] = [];
  return of(action.payload).pipe(
    mergeMap((actionInPayload) => {
      // Saving work
      return executeSaveAssignmentRequest(actionInPayload, state$);
    }),
    concatMap(
      (
        chainedData:
          | (ActionWithPayload<PerformAndSaveActionObject> & {
              actionInPayload: {
                isProcessAction: boolean;
                containerItemID: string;
                RemoteSystemID?: string;
                context: string;
                caseID: string;
              };
              response: { data: { caseInfo: any } };
              headers: AxiosResponse['headers'];
            })
          | (ErrorAction & {
              actionInPayload: {
                isProcessAction: boolean;
                containerItemID: string;
                RemoteSystemID?: string;
                context: string;
                caseID: string;
              };
              response: { data: { caseInfo: any } };
              headers: AxiosResponse['headers'];
            })
      ) => {
        if (chainedData && isErrorActionType(chainedData.type)) {
          return of(chainedData);
        }
        const { containerItemID } = chainedData && chainedData.actionInPayload;
        const parentContainerItemID = getParentContextName(containerItemID);
        const parentTarget = getTargetFromContainerItemID(parentContainerItemID);
        const parentContainersData = getContainerData(parentTarget);
        const childContainers = getChildContainers(parentContainerItemID)[0];
        let caseWorkAreaContext = childContainers
          ? getActiveContainerItemName(getChildContainers(parentContainerItemID)[0])
          : parentContainerItemID;
        const isContainerMultiDoc = !isContainerSingleMode(getTargetFromContainerItemID(caseWorkAreaContext));
        if (isContainerMultiDoc) {
          const containersData = getContainerData(childContainers);
          containersData?.accessedOrder.forEach((e: string | number) => {
            if (chainedData.actionInPayload.caseID === containersData.items[e].key) {
              caseWorkAreaContext = containersData.items[e].context;
            }
          });
        }
        const activeContainerItem = getActiveContainerItemName(parentTarget);
        if (action.payload.canUpdateAvailableActions) {
          updateAvailableActionsInResponse(chainedData.response);
        }

        const createETagUpdateAction = (context: string) => {
          return actions.postProcess(actions.mergeData, {
            context,
            data: {
              caseInfo: {
                ...(chainedData.response.data?.caseInfo || {}),
                headers: chainedData.headers
              }
            }
          });
        };

        if (caseWorkAreaContext) {
          actionsOut.push(createETagUpdateAction(caseWorkAreaContext));
        }
        if (
          parentContainersData?.items?.[activeContainerItem as string]?.key === chainedData?.actionInPayload?.caseID
        ) {
          const actionPayload = {
            context: parentContainerItemID,
            data: {
              ...chainedData.response.data,
              caseInfo: {
                ...(chainedData.response.data?.caseInfo || {}),
                headers: chainedData.headers
              }
            }
          };
          const propertiesToReplace = CaseUtils.getPropertiesToReplace(containerItemID);
          actionsOut.push(actions.postProcess(actions.mergeData, actionPayload, { propertiesToReplace }));
        }
        actionsOut.push(actions.postProcess(actions.updateContainerItem, { context: containerItemID }));

        deferCaseSummaryViewUpdate(chainedData, actionsOut);

        /* releasing lock on assignment */
        return executeReleaseLockRequest({
          ...chainedData.actionInPayload,
          RemoteSystemID: RemoteCase.getRemoteHeaderByContext(chainedData.actionInPayload.context) ?? undefined
        });
      }
    ),
    mergeMap(async (chainedData) => {
      if (chainedData && isErrorActionType(chainedData.type)) {
        const { payload } = chainedData;
        const errorAction = processDataToDispatchErrorAction({ ...chainedData, ...payload, actionMgrID });
        actionsOut.push(errorAction);
        const modalLoadingContainer = saveActionObject.action.payload.modalLoadingContainer;
        if (modalLoadingContainer) {
          const modelContext = modalLoadingContainer.context;
          const { context_data: contextData } = StateManager.getDataObject(modelContext) || {};
          if (contextData?.isLoading) {
            closeContainerItem(modelContext);
          }
        }
        return actionsOut;
      }

      const { actionInPayload } = chainedData;
      const { context, containerName } = actionInPayload;
      const target = getResolvedTargetName(containerName, context);
      const payload = { target, containerItemID: context, context };

      if (!saveOnly) {
        actionsOut.push(actions.postProcess(actions.removeContainerItem, payload));
      }
      return actionsOut;
    }),
    map(() => {
      StateManager.dispatch(actionsOut);
      promiseHandler(actionsOut, actionMgrID);

      // Publish event after actions are dispatched
      publishSaveAssignmentCompleteEvent({
        context: action.payload.context,
        assignmentID: action.payload.assignmentID
      });

      if (!saveOnly) {
        actionsOut = [];
      }
      if (ActionsSequencer.isDeferredActionInProgress(action.payload.context)) {
        ActionsSequencer.handleDeferredActionCompletion(action.payload.context);
      }
      return actionsOut;
    })
  );
};

const saveAndCloseAssignment = (action$: Observable<Action>, state$: StateObservable<State>) => {
  let actionMgrID = 0;
  let caseType;
  return action$.pipe(
    ofType(SAVE_AND_CLOSE),
    switchMap((action: ActionWithPayload<PerformAndSaveActionPayload>): Observable<Action[]> => {
      const { saveOnly } = action.payload;
      ({ actionMgrID, caseType } = action.payload);
      const saveActionObject: PerformAndSaveActionObject = { action, state$, actionMgrID, caseType };
      return performSave(saveActionObject, saveOnly);
    })
  );
};

export default saveAndCloseAssignment;
